export const YANDEX_METRICA_COUNTER_ID = 108684898;
const YANDEX_METRICA_TAG_URL = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRICA_COUNTER_ID}`;

type YmInitOptions = {
  defer: boolean;
  ssr: boolean;
  webvisor: boolean;
  clickmap: boolean;
  trackLinks: boolean;
  accurateTrackBounce: boolean;
};

type YmHitOptions = {
  title?: string;
  referer?: string;
};

type YandexGoalParamValue = string | number | boolean;

export type YandexGoalId =
  | 'catalog_club_open'
  | 'club_purchase_click'
  | 'purchase_intent_submit'
  | 'purchase_success_view'
  | 'club_read_80'
  | 'demo_read_open'
  | 'demo_purchase_click'
  | 'demo_purchase_success_view';

export type YandexGoalParams = Partial<{
  club_id: string;
  club_slug: string;
  chapter_id: string;
  source_surface: string;
  demo_context: string;
  progress_percent: number;
  milestone: string;
  has_demo: boolean;
  order_price: number;
  currency: string;
}>;

type YmCall =
  | [number, 'init', YmInitOptions]
  | [number, 'hit', string, YmHitOptions]
  | [number, 'reachGoal', YandexGoalId]
  | [number, 'reachGoal', YandexGoalId, Record<string, YandexGoalParamValue>];

type YmFunction = ((...args: YmCall) => void) & {
  a?: YmCall[];
  l?: number;
};

declare global {
  interface Window {
    ym?: YmFunction;
  }
}

const initOptions: YmInitOptions = {
  defer: true,
  ssr: true,
  webvisor: true,
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
};

let initialized = false;
let lastTrackedUrl: string | null = null;
const trackedGoalKeys = new Set<string>();
const GOAL_DEDUPE_STORAGE_KEY = 'inhound:yandex-metrica:dedupe';

function readTrackedGoalKeysFromSession() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.sessionStorage.getItem(GOAL_DEDUPE_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    window.sessionStorage.removeItem(GOAL_DEDUPE_STORAGE_KEY);
    return [];
  }
}

function writeTrackedGoalKeysToSession() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(GOAL_DEDUPE_STORAGE_KEY, JSON.stringify([...trackedGoalKeys]));
  } catch {
    // Ignore storage failures to avoid breaking user flows.
  }
}

function hasTrackedGoalKey(goalKey: string) {
  if (trackedGoalKeys.has(goalKey)) {
    return true;
  }

  for (const storedKey of readTrackedGoalKeysFromSession()) {
    trackedGoalKeys.add(storedKey);
  }

  return trackedGoalKeys.has(goalKey);
}

function rememberTrackedGoalKey(goalKey: string) {
  trackedGoalKeys.add(goalKey);
  writeTrackedGoalKeysToSession();
}

function sanitizeGoalParams(params?: YandexGoalParams) {
  if (!params) {
    return undefined;
  }

  const sanitizedEntries = Object.entries(params).filter(([, value]) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  });

  if (sanitizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(sanitizedEntries) as Record<string, YandexGoalParamValue>;
}

function createYmStub() {
  if (typeof window === 'undefined' || window.ym) {
    return;
  }

  const stub: YmFunction = ((...args: YmCall) => {
    (stub.a = stub.a ?? []).push(args);
  }) as YmFunction;
  stub.l = Date.now();
  window.ym = stub;
}

function ensureTagScript() {
  if (typeof document === 'undefined') {
    return;
  }

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src="${YANDEX_METRICA_TAG_URL}"]`,
  );
  if (existingScript) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = YANDEX_METRICA_TAG_URL;
  script.dataset.yandexMetricaTag = String(YANDEX_METRICA_COUNTER_ID);

  const firstScript = document.scripts[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
}

function toTrackedUrl(url: string) {
  if (typeof window === 'undefined') {
    return url;
  }

  return new URL(url, window.location.origin).href;
}

function isYandexMetricaDisabled() {
  return import.meta.env['VITE_YANDEX_METRICA_DISABLED'] === 'true';
}

export function initYandexMetrica() {
  if (isYandexMetricaDisabled()) {
    return false;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  createYmStub();
  ensureTagScript();

  if (!initialized) {
    window.ym?.(YANDEX_METRICA_COUNTER_ID, 'init', initOptions);
    initialized = true;
  }

  return true;
}

export function trackYandexPageView(url: string, title = document.title) {
  if (!initYandexMetrica()) {
    return false;
  }

  const trackedUrl = toTrackedUrl(url);
  if (lastTrackedUrl === trackedUrl) {
    return false;
  }

  const referer = lastTrackedUrl ?? document.referrer ?? undefined;
  const hitOptions: YmHitOptions = {
    title,
  };
  if (referer) {
    hitOptions.referer = referer;
  }

  window.ym?.(YANDEX_METRICA_COUNTER_ID, 'hit', trackedUrl, hitOptions);
  lastTrackedUrl = trackedUrl;
  return true;
}

export function trackYandexGoal(
  goalId: YandexGoalId,
  params?: YandexGoalParams,
  options?: {
    dedupeKey?: string;
  },
) {
  if (!initYandexMetrica()) {
    return false;
  }

  const goalKey = options?.dedupeKey ? `${goalId}:${options.dedupeKey}` : null;
  if (goalKey && hasTrackedGoalKey(goalKey)) {
    return false;
  }

  const sanitizedParams = sanitizeGoalParams(params);
  if (sanitizedParams) {
    window.ym?.(YANDEX_METRICA_COUNTER_ID, 'reachGoal', goalId, sanitizedParams);
  } else {
    window.ym?.(YANDEX_METRICA_COUNTER_ID, 'reachGoal', goalId);
  }

  if (goalKey) {
    rememberTrackedGoalKey(goalKey);
  }

  return true;
}

export function resetYandexMetricaForTests() {
  initialized = false;
  lastTrackedUrl = null;
  trackedGoalKeys.clear();

  if (typeof window !== 'undefined') {
    delete window.ym;
    window.sessionStorage.removeItem(GOAL_DEDUPE_STORAGE_KEY);
  }

  if (typeof document !== 'undefined') {
    document
      .querySelectorAll(`script[src="${YANDEX_METRICA_TAG_URL}"]`)
      .forEach((script) => script.remove());
  }
}
